import { Typography, useTheme } from "@mui/material";
import maintenance from "assets/animations/maintenance.json" with { type: "json" };
import { ModalContentWrapper } from "components/primitives/ModalContentWrapper";
import { ModalTitle } from "components/primitives/ModalTitle";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useOrder } from "hooks/useOrder";
import { useAppDispatch, useAppSelector } from "hooks/utilsHooks/storeHooks";
import Lottie from "lottie-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { selectShortFridgeId } from "slices/fridgeInfoSlice";
import { dismissRefill, selectOrder, selectRefill } from "slices/transactionSlice";
import { TransactionState } from "types";
import { logToSentry } from "utils/logger";
import { BasicModal } from "./BasicModal";
import { ModalSupportFooter } from "./ModalSupportFooter";

const RemoteRefillModalContent = () => {
	const fridgeId = useAppSelector(selectShortFridgeId);
	const { t } = useTranslation();
	const theme = useTheme();
	const { trackRemoteRefillModal } = useMixpanelTrackers();

	useEffect(() => {
		logToSentry(`Remote refill modal displayed on fridge: ${fridgeId}`, "info");
		trackRemoteRefillModal("open");

		return () => {
			trackRemoteRefillModal("close");
		};
	}, [fridgeId, trackRemoteRefillModal]);

	return (
		<ModalContentWrapper>
			<ModalTitle variant="promoHeader">{t("modals.remoteRefill.title" as never)}</ModalTitle>
			<Typography variant="promoText" sx={{ marginRight: "auto", marginBottom: 10 }}>
				{t("modals.remoteRefill.description" as never)}
			</Typography>
			<Lottie animationData={maintenance} loop={true} />
			<ModalSupportFooter fridgeId={fridgeId} background={theme.palette.copper.main} />
		</ModalContentWrapper>
	);
};

const FINISH_ORDER_DELAY_MS = 10_000;

export const RemoteRefillModal = () => {
	const refill = useAppSelector(selectRefill);
	const order = useAppSelector(selectOrder);
	const { finishOrder } = useOrder();
	const appDispatch = useAppDispatch();
	const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
	const orderIdAtWindDownRef = useRef<string | null | undefined>(undefined);

	useEffect(() => {
		if (refill?.state !== TransactionState.Finished) {
			orderIdAtWindDownRef.current = undefined;
			return;
		}

		// Capture order ID when refill first transitions to Finished
		if (orderIdAtWindDownRef.current === undefined) {
			orderIdAtWindDownRef.current = order?.id ?? null;
		}

		// New order started during wind-down — dismiss refill modal immediately
		// without calling finishOrder (which would destroy the new order)
		if (order?.id && order.id !== orderIdAtWindDownRef.current) {
			clearTimeout(timerRef.current);
			appDispatch(dismissRefill());
			return;
		}

		timerRef.current = setTimeout(() => {
			finishOrder();
		}, FINISH_ORDER_DELAY_MS);

		return () => {
			clearTimeout(timerRef.current);
		};
	}, [refill?.state, order?.id, finishOrder, appDispatch]);

	return <BasicModal modalContent={<RemoteRefillModalContent />} open={true} closable={false} fullHeight={true} />;
};
